import { z } from 'zod';
import { SchedulingAvailability, SchedulingHost } from '../scheduling/scheduling.js';
import { ProfileAvatarDesign, ProfileAvatarUpdate } from './avatar.js';
import { Member } from './user.js';
export const ProfileLocale = z.enum(['zh', 'en']);
export const ProfileTextSource = z.enum(['empty', 'manual', 'ai']);
export const PROFILE_TEXT_FIELDS = [
    'headline',
    'bio',
    'status',
    'availabilityNote',
    'signature',
];
export const ProfileTextField = z.enum(PROFILE_TEXT_FIELDS);
const IsoDateTime = z.string().datetime({ offset: true });
const HttpUrl = z
    .string()
    .trim()
    .url()
    .max(2048)
    .refine((value) => {
    if (hasC0Control(value))
        return false;
    try {
        const parsed = new URL(value);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    }
    catch {
        return false;
    }
}, 'URL must use http or https.');
function hasC0Control(value) {
    for (let index = 0; index < value.length; index += 1) {
        const code = value.charCodeAt(index);
        if (code <= 0x1f || code === 0x7f)
            return true;
    }
    return false;
}
export const ProfileLocalizedText = z.object({
    value: z.string(),
    source: ProfileTextSource,
    updatedAt: IsoDateTime.nullable(),
});
export const ProfileLocalizedPair = z.object({
    zh: ProfileLocalizedText,
    en: ProfileLocalizedText,
});
export const ProfileTexts = z.object({
    headline: ProfileLocalizedPair,
    bio: ProfileLocalizedPair,
    status: ProfileLocalizedPair,
    availabilityNote: ProfileLocalizedPair,
    signature: ProfileLocalizedPair,
});
export const ProfileLink = z.object({
    label: z.string().trim().min(1).max(40),
    url: HttpUrl,
});
export const ProfileTheme = z.object({
    accent: z.enum(['iris', 'jade', 'amber', 'rose', 'slate']),
    banner: z.enum(['paper', 'solid', 'wash', 'minimal']),
    layout: z.enum(['editorial', 'studio', 'compact']),
    density: z.enum(['comfortable', 'dense']),
});
export const ProfileContactPreference = z.enum(['site', 'email', 'phone', 'none']);
export const ProfilePreferences = z.object({
    contact: ProfileContactPreference,
    showEmail: z.boolean(),
    showPhone: z.boolean(),
    showCity: z.boolean(),
    showArticles: z.boolean(),
    showSchedule: z.boolean(),
});
export const MemberHomepageProfile = z.object({
    userId: z.string().uuid(),
    preferredLocale: ProfileLocale,
    city: z.string().max(120).nullable(),
    website: HttpUrl.nullable(),
    texts: ProfileTexts,
    interests: z.array(z.string().min(1).max(40)).max(16),
    links: z.array(ProfileLink).max(8),
    avatar: ProfileAvatarDesign,
    theme: ProfileTheme,
    preferences: ProfilePreferences,
    createdAt: IsoDateTime.nullable(),
    updatedAt: IsoDateTime.nullable(),
});
export const ProfileRendered = z.object({
    locale: ProfileLocale,
    fallbackLocale: ProfileLocale.nullable(),
    headline: z.string(),
    bio: z.string(),
    status: z.string(),
    availabilityNote: z.string(),
    signature: z.string(),
});
export const ProfileArticleSummary = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    title: z.string(),
    summary: z.string(),
    coverImageUrl: z.string().nullable(),
    status: z.string(),
    publishedAt: IsoDateTime.nullable(),
    updatedAt: IsoDateTime,
});
export const ProfileSchedulingSummary = z.object({
    host: SchedulingHost.nullable(),
    availability: z.array(SchedulingAvailability),
});
export const ProfileContactSummary = z.object({
    city: z.string().max(120).nullable(),
    website: HttpUrl.nullable(),
    email: z.string().email().nullable(),
    phone: z.string().nullable(),
    preference: ProfileContactPreference,
});
export const MemberHomepageResponse = z.object({
    member: Member,
    profile: MemberHomepageProfile,
    rendered: ProfileRendered,
    contact: ProfileContactSummary,
    articles: z.array(ProfileArticleSummary),
    scheduling: ProfileSchedulingSummary,
    viewer: z.object({
        canEdit: z.boolean(),
        canManage: z.boolean(),
    }),
});
const ProfileTextPatch = z.object({
    headline: z.string().max(160).optional(),
    bio: z.string().max(2000).optional(),
    status: z.string().max(120).optional(),
    availabilityNote: z.string().max(1000).optional(),
    signature: z.string().max(240).optional(),
});
export const UpdateMemberHomepageProfileRequest = z.object({
    displayName: z.string().trim().min(1).max(80).optional(),
    preferredLocale: ProfileLocale.optional(),
    locale: ProfileLocale,
    city: z.string().trim().max(120).nullable().optional(),
    website: HttpUrl.nullable().optional(),
    fields: ProfileTextPatch.optional(),
    interests: z.array(z.string().trim().min(1).max(40)).max(16).optional(),
    links: z.array(ProfileLink).max(8).optional(),
    avatar: ProfileAvatarUpdate.optional(),
    theme: ProfileTheme.partial().optional(),
    preferences: ProfilePreferences.partial().optional(),
});
export const TranslateMemberHomepageProfileRequest = z.object({
    targetLocale: ProfileLocale,
});
//# sourceMappingURL=profile.js.map